// apps/client/src/hooks/useBrokerCommissionConfig.ts
// Real-time Firestore listener for broker commission configuration.
// Also fetches monthly comms and broker loans from BigQuery via callable.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  query,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { mapFirestoreConfigDoc } from './mapFirestoreConfigDoc';
import type { FirestoreCommissionConfig, CommissionConfigWithMeta } from './mapFirestoreConfigDoc';

// Re-export so existing imports keep working:
export type { CommissionConfigWithMeta } from './mapFirestoreConfigDoc';

export interface BrokerLoanFromBQ {
  loanId: string;
  customerName: string | null;
  lender: string | null;
  paymentType: string | null;
  commissionCents: number;
  periodMonth: string;
  isArrears: boolean;
  isDischarged: boolean;
  /** Third-party referrer fee, split out of the commission group so it never
   *  reduces the broker's share base. Only ever true for a tenant on
   *  `featureFlags.referrer_deduction_treatment = 'company_borne'`. */
  isReferrerDeduction?: boolean;
  /** Settled loan amount on upfront, current balance on trail. `null` when the
   *  source cell was blank; ABSENT when the caller has `hideLoanAmounts`. */
  loanValueCents?: number | null;
}

export interface RecipientCommissionRow {
  rowId: string;
  brokerDocId: string;
  loanId: string;
  customerName: string | null;
  lender: string | null;
  paymentType: string;
  periodMonth: string;
  recipientCommissionCents: number;
  effectiveRateBps: number | null;
  grossCommissionCents?: number;
  introductionType: 'broker' | 'company' | 'self_sourced' | null;
  isEstimated: boolean;
  isArrears: boolean;
  isDischarged: boolean;
  /** Third-party referrer fee: shown, never part of the split base. */
  isReferrerDeduction?: boolean;
  /** Settled amount on upfront, current balance on trail. `null` when blank;
   *  ABSENT when the caller has `hideLoanAmounts` (never zeroed). */
  loanValueCents?: number | null;
  isManualAdjustment: boolean;
  manualAdjustmentId?: string;
}

export interface RecipientCommissionGroup {
  brokerDocId: string;
  brokerName: string;
  rows: RecipientCommissionRow[];
  recipientTotalCents: number;
  grossTotalCents?: number;
}

export interface RecipientCommissionView {
  role: 'broker' | 'broker_assistant' | 'credit_analyst';
  grossVisibility: { upfront: boolean; trail: boolean };
  groups: RecipientCommissionGroup[];
  recipientTotalCents: number;
}

interface BrokerCommissionConfigOptions {
  /** Request the server-authoritative role-correct self-view payload. */
  includeRecipientView?: boolean;
  /** Skip legacy/config reads; used by assistant and analyst read-only UI. */
  recipientOnly?: boolean;
}

interface UseBrokerCommissionConfigReturn {
  currentConfig: CommissionConfigWithMeta | null;
  configHistory: CommissionConfigWithMeta[];
  monthlyComms: { month: string; cents: number }[];
  brokerLoans: BrokerLoanFromBQ[];
  recipientView: RecipientCommissionView | null;
  recipientViewLoading: boolean;
  recipientViewError: string | null;
  trailStartMonth: string | null;
  loading: boolean;
  error: string | null;
  /**
   * Force a re-fetch of BigQuery-backed data (monthlyComms, brokerLoans,
   * trailStartMonth). Use this after mutations that change the trail-start
   * cutoff so the UI reflects the new filter without a full page reload.
   * Firestore-backed fields (currentConfig, configHistory) are already live
   * via onSnapshot and don't need manual invalidation.
   */
  refetchBigQueryData: () => Promise<void>;
}

/**
 * Real-time listener on `companies/{companyId}/brokers/{brokerDocId}/commission_config`
 * ordered by effectiveFrom desc.
 *
 * Also fetches monthly comms and broker loans from BigQuery (one-time callable).
 */
export function useBrokerCommissionConfig(
  companyId: string | undefined,
  brokerDocId: string | undefined,
  options: BrokerCommissionConfigOptions = {},
): UseBrokerCommissionConfigReturn {
  const [currentConfig, setCurrentConfig] = useState<CommissionConfigWithMeta | null>(null);
  const [configHistory, setConfigHistory] = useState<CommissionConfigWithMeta[]>([]);
  const [monthlyComms, setMonthlyComms] = useState<{ month: string; cents: number }[]>([]);
  const [brokerLoans, setBrokerLoans] = useState<BrokerLoanFromBQ[]>([]);
  const [recipientView, setRecipientView] = useState<RecipientCommissionView | null>(null);
  const [recipientViewLoading, setRecipientViewLoading] = useState(
    options.includeRecipientView === true,
  );
  const [recipientViewError, setRecipientViewError] = useState<string | null>(null);
  const [trailStartMonth, setTrailStartMonth] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Stale-while-revalidate for the recipient view: the full-page loading
  // state is reserved for the FIRST load of an identity. Background
  // refetches (e.g. awaiting refetchBigQueryData() after a tag write) keep
  // the rendered table; flipping recipientViewLoading there unmounts the
  // page content and resets scroll to the top.
  const recipientViewLoadedOnceRef = useRef(false);
  useEffect(() => {
    recipientViewLoadedOnceRef.current = false;
  }, [companyId, brokerDocId, options.recipientOnly, options.includeRecipientView]);

  useEffect(() => {
    if (!companyId || !brokerDocId || options.recipientOnly) {
      setCurrentConfig(null);
      setConfigHistory([]);
      setTrailStartMonth(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const colRef = collection(
      db,
      `companies/${companyId}/brokers/${brokerDocId}/commission_config`
    );
    const q = query(colRef, orderBy('effectiveFrom', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const configs: CommissionConfigWithMeta[] = [];

        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as FirestoreCommissionConfig;
          configs.push(mapFirestoreConfigDoc(docSnap.id, data));
        });

        // Resolve UIDs to display names
        const uids = [...new Set(configs.map(c => c.createdBy).filter(Boolean))];
        if (uids.length > 0) {
          const nameMap: Record<string, string> = {};
          await Promise.all(
            uids.map(async (uid) => {
              try {
                const userDoc = await getDoc(doc(db, 'users', uid));
                if (userDoc.exists()) {
                  const data = userDoc.data();
                  nameMap[uid] = data.displayName || data.email || uid;
                }
              } catch { /* ignore — fallback to UID */ }
            })
          );
          for (const c of configs) {
            if (c.createdBy && nameMap[c.createdBy]) {
              c.createdBy = nameMap[c.createdBy];
            }
          }
        }

        // Current config = effectiveTo is null (currently active)
        const active = configs.find(c => c.effectiveTo === null) ?? null;

        setCurrentConfig(active);
        setConfigHistory(configs);
        setLoading(false);
      },
      (err) => {
        console.error('[useBrokerCommissionConfig] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load commission config');
        setCurrentConfig(null);
        setConfigHistory([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyId, brokerDocId, options.recipientOnly]);

  // Fetch monthly comms + broker loans from BigQuery via callable. Wrapped in
  // useCallback so the save handler can invoke it after updating config.
  const refetchBigQueryData = useCallback(async () => {
    if (!companyId || (!brokerDocId && !options.recipientOnly)) {
      setMonthlyComms([]);
      setBrokerLoans([]);
      setTrailStartMonth(null);
      setRecipientView(null);
      setRecipientViewLoading(false);
      return;
    }
    if (options.includeRecipientView) {
      if (!recipientViewLoadedOnceRef.current) setRecipientViewLoading(true);
      setRecipientViewError(null);
    }
    try {
      const getConfig = httpsCallable(functions, 'getBrokerCommissionConfig');
      const result = await getConfig({
        companyId,
        ...(brokerDocId ? { brokerDocId } : {}),
        includeMonthlyComms: !options.recipientOnly,
        includeBrokerLoans: !options.recipientOnly,
        includeRecipientView: options.includeRecipientView === true,
      });
      const data = result.data as {
        monthlyComms?: { month: string; cents: number }[];
        brokerLoans?: BrokerLoanFromBQ[];
        trailStartMonth?: string | null;
        recipientView?: RecipientCommissionView | null;
      };
      setMonthlyComms(data.monthlyComms || []);
      setBrokerLoans(data.brokerLoans || []);
      setTrailStartMonth(data.trailStartMonth ?? null);
      if (options.includeRecipientView) {
        setRecipientView(data.recipientView ?? null);
        if (!data.recipientView) {
          setRecipientViewError('Recipient commission data was not returned');
        }
      }
    } catch (err) {
      console.error('[useBrokerCommissionConfig] BigQuery data fetch error:', err);
      // Non-fatal — reconciliation and loan tree stay empty
      if (options.includeRecipientView) {
        setRecipientView(null);
        setRecipientViewError(
          err instanceof Error ? err.message : 'Failed to load recipient commission data',
        );
      }
    } finally {
      if (options.includeRecipientView) {
        recipientViewLoadedOnceRef.current = true;
        setRecipientViewLoading(false);
      }
    }
  }, [
    companyId,
    brokerDocId,
    options.includeRecipientView,
    options.recipientOnly,
  ]);

  useEffect(() => {
    refetchBigQueryData();
  }, [refetchBigQueryData]);

  return {
    currentConfig,
    configHistory,
    monthlyComms,
    brokerLoans,
    recipientView,
    recipientViewLoading,
    recipientViewError,
    trailStartMonth,
    loading,
    error,
    refetchBigQueryData,
  };
}
